import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';

const fmt = (d) => { try { return new Date(d).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }); } catch { return ''; } };

// Mandatory compliance pop-up. Zero-bypass: no localStorage/sessionStorage suppression.
// "Acknowledge & Close for this View" only hides it in component state; it returns on the next mount/reload
// until the Principal resolves/dismisses the complaint in the database.
export default function ComplianceAlertModal({ user, isLiveClassActive }) {
  const [data, setData] = useState({ hasActiveAlert: false, totalCount: 0, alerts: [], broadcasts: [] });
  const [closed, setClosed] = useState(false);
  const uid = String(user?._id || user?.id || '');

  const load = useCallback(() => {
    if (!uid) return Promise.resolve();
    return axios.get(`${API_BASE_URL}/api/staff-feedback/teacher-status`, { headers: { 'x-user-id': uid } })
      .then((r) => setData({ broadcasts: [], ...r.data }))
      .catch(() => {});
  }, [uid]);

  // On mount and every time a live class ends, re-check the DB so the pop-up reflects the latest state.
  useEffect(() => { load(); }, [load, isLiveClassActive]);

  const hasContent = data.hasActiveAlert || (data.broadcasts || []).length > 0;
  // Suppressed (not dismissed) while a live class is in session; appears immediately once it ends.
  if (!hasContent || closed || isLiveClassActive) return null;

  const topics = [...new Set(data.alerts.flatMap((a) => a.categories || []))];

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      style={{ position: 'fixed', top: 0, right: 0, bottom: 0, left: 0, zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,.7)', padding: 16 }}
      role="alertdialog" aria-modal="true" aria-labelledby="compliance-alert-title"
    >
      <div
        className="w-full max-w-lg rounded-2xl bg-white shadow-2xl border-4 border-red-600"
        style={{ width: '100%', maxWidth: 520, maxHeight: '92dvh', display: 'flex', flexDirection: 'column', overflow: 'hidden', background: '#fff', borderRadius: 16, border: '4px solid #dc2626', boxShadow: '0 20px 50px rgba(0,0,0,.5)' }}
      >
        <div style={{ flex: 'none', background: '#dc2626', color: '#fff', padding: '14px 18px' }}>
          <h2 id="compliance-alert-title" style={{ margin: 0, fontSize: 18, fontWeight: 800 }}>🚨 Staff Compliance &amp; Behavioral Advisory Notice</h2>
          <div style={{ fontSize: 12, opacity: 0.9, marginTop: 2 }}>Class / Profile • Visible to you and the Principal</div>
        </div>
        <div style={{ flex: '1 1 auto', minHeight: 0, overflowY: 'auto', overscrollBehaviorY: 'contain', WebkitOverflowScrolling: 'touch', touchAction: 'pan-y', padding: 18 }}>
          {data.hasActiveAlert && (
            <>
              <span style={{ display: 'inline-block', background: '#fee2e2', color: '#b91c1c', fontWeight: 800, fontSize: 12, padding: '3px 10px', borderRadius: 999, border: '1px solid #fca5a5' }}>🔴 Action Required</span>
              <p style={{ margin: '10px 0 0', padding: 10, background: '#fef3c7', border: '1px solid #f59e0b', borderRadius: 8, color: '#78350f', fontWeight: 600 }}>
                Attention: {data.totalCount} complaint(s)/concern(s) have been registered regarding your conduct/compliance.
              </p>
              {topics.length > 0 && <p style={{ fontSize: 12, color: '#475569' }}>Categories: <b>{topics.join(' / ')}</b></p>}
              {data.alerts.map((a) => (
                <div key={a._id} style={{ marginTop: 10, padding: 10, border: '1px solid #fecaca', background: '#fef2f2', borderRadius: 8 }}>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                    {(a.categories || []).map((c) => <span key={c} style={{ background: '#dc2626', color: '#fff', borderRadius: 999, padding: '2px 8px', fontSize: 11, fontWeight: 700 }}>{c}</span>)}
                  </div>
                  <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>🕒 Incident: {fmt(a.incidentDate)}</div>
                  {a.reporterName && <div style={{ fontSize: 12, marginTop: 2 }}>Reported by: <b>{a.reporterName}</b></div>}
                  <p style={{ margin: '6px 0 0', whiteSpace: 'pre-wrap', fontSize: 14, color: '#1e293b' }}><b>Remarks:</b> {a.remarks}</p>
                  {a.broadcastNote && <p style={{ margin: '4px 0 0', fontSize: 12, fontStyle: 'italic' }}>Principal's note: {a.broadcastNote}</p>}
                </div>
              ))}
            </>
          )}
          {(data.broadcasts || []).length > 0 && (
            <div style={{ marginTop: 12, padding: 10, background: '#eff6ff', border: '1px solid #93c5fd', borderRadius: 8 }}>
              <b style={{ fontSize: 13 }}>📢 Principal's advisory to all staff: please maintain standard school decorum.</b>
              {data.broadcasts.map((b) => (
                <div key={b._id} style={{ fontSize: 12, marginTop: 4, color: '#334155' }}>
                  {(b.categories || []).join(' / ')} · {fmt(b.incidentDate)}{b.broadcastNote ? ` — ${b.broadcastNote}` : ''}
                </div>
              ))}
            </div>
          )}
          <button
            onClick={() => setClosed(true)}
            style={{ marginTop: 16, width: '100%', padding: 12, border: 'none', borderRadius: 12, background: '#dc2626', color: '#fff', fontWeight: 700, cursor: 'pointer', fontSize: 15 }}
          >
            Acknowledge &amp; Close for this View
          </button>
          <p style={{ fontSize: 11, color: '#64748b', textAlign: 'center', margin: '6px 0 0' }}>This notice will reappear until the Principal resolves the matter.</p>
        </div>
      </div>
    </div>
  );
}
